import React, { useState } from "react";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
    ResponsiveContainer,
} from "recharts";
import { Checkbox, FormControlLabel, FormGroup, Collapse, IconButton } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WindDirectionArrow, { getCardinalDirection } from './WindDirectionArrow';

const CustomizedDot = (props) => {
    const { cx, cy, stroke, value, dataKey, isActive } = props;

    // Safety check: if value is null, undefined, or NaN, do not render a dot
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    if (value === null || value === undefined || isNaN(numericValue)) {
        return null;
    }

    // Default rendering (no highlight logic used in yearly chart currently)
    if (dataKey && dataKey.toLowerCase().startsWith('wd')) {
        return <WindDirectionArrow cx={cx} cy={cy} value={numericValue} stroke={stroke} size={isActive ? 20 : 16} />;
    }

    // NEW: Check if this is a wind speed parameter and direction is also visible
    if (dataKey && dataKey.toLowerCase().startsWith('ws')) {
        const dirKey = dataKey.replace(/^ws/, 'wd').replace(/Mps$/, 'Deg');
        // payload is available in dot props in recharts
        const payloadData = props.payload;
        if (props.visibleParams && props.visibleParams[dirKey] && payloadData && payloadData[dirKey] !== undefined) {
            const dirValue = typeof payloadData[dirKey] === 'number' ? payloadData[dirKey] : parseFloat(payloadData[dirKey]);
            if (!isNaN(dirValue)) {
                const dirColor = ALL_FIELDS.find(f => f.key === dirKey)?.color || stroke;
                return <WindDirectionArrow cx={cx} cy={cy} value={dirValue} stroke={dirColor} size={isActive ? 20 : 16} />;
            }
        }
    }

    return <circle cx={cx} cy={cy} r={isActive ? 6 : 4} fill={stroke} />;
};

const CustomTooltip = ({ active, payload, label, visibleParams }) => {
    if (active && payload && payload.length) {
        return (
            <div className="glass-panel" style={{
                padding: '10px',
                backgroundColor: 'rgba(255,255,255,0.95)',
                border: '1px solid rgba(0,0,0,0.1)',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                <div style={{ fontWeight: 600, marginBottom: '5px', color: '#1F5A36' }}>
                    Year: {label}
                </div>
                {payload.map((entry, idx) => {
                    const isWindSpeed = entry.dataKey && entry.dataKey.toLowerCase().startsWith('ws');
                    const dirKey = isWindSpeed ? entry.dataKey.replace(/^ws/, 'wd').replace(/Mps$/, 'Deg') : null;
                    const showDir = dirKey && visibleParams && visibleParams[dirKey] && entry.payload[dirKey] !== undefined;

                    return (
                        <div
                            key={`item-${idx}`}
                            style={{
                                color: entry.color,
                                fontSize: '0.85rem',
                                marginTop: '2px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px',
                                flexWrap: 'wrap'
                            }}
                        >
                            <span>{entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}</span>
                            {showDir && (
                                <span style={{
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    backgroundColor: 'rgba(0,0,0,0.03)',
                                    padding: '0 4px',
                                    borderRadius: '4px',
                                    color: ALL_FIELDS.find(f => f.key === dirKey)?.color || '#1F5A36'
                                }}>
                                    ({entry.payload[dirKey].toFixed(2)}° {getCardinalDirection(entry.payload[dirKey])})
                                    <svg width="12" height="12" viewBox="0 0 16 16" style={{ transform: `rotate(${entry.payload[dirKey]}deg)` }}>
                                        <WindDirectionArrow
                                            cx={8}
                                            cy={8}
                                            value={0}
                                            stroke={ALL_FIELDS.find(f => f.key === dirKey)?.color || '#1F5A36'}
                                            size={12}
                                        />
                                    </svg>
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
    return null;
};

// All yearly fields
const FIELDS_2M = [
    { key: 'at2mDegC', label: 'Temperature (°C)', color: '#EF6C00' },
    { key: 'rh2mPer', label: 'Humidity (%)', color: '#1565C0' },
    { key: 'rainFallMm', label: 'Rainfall (mm)', color: '#2E7D32' },
    { key: 'avgNetRadiationwBym2', label: 'Net Radiation', color: '#7B1FA2' },
    { key: 'ws2mMps', label: 'Wind Spd (m/s)', color: '#4E342E' },
    { key: 'wd2mDeg', label: 'Wind Dir (°)', color: '#4ce35eff' },
    { key: 'ap2mHpa', label: 'Pressure (hPa)', color: '#311B92' }
];

const FIELDS_4M = [
    { key: 'at4mDegC', label: 'Temperature (°C)', color: '#EF6C00' },
    { key: 'rh4mPer', label: 'Humidity (%)', color: '#1565C0' },
    { key: 'rainFallMm', label: 'Rainfall (mm)', color: '#2E7D32' },
    { key: 'avgNetRadiationwBym2', label: 'Net Radiation', color: '#7B1FA2' },
    { key: 'ws4mMps', label: 'Wind Spd (m/s)', color: '#4E342E' },
    { key: 'wd4mDeg', label: 'Wind Dir (°)', color: '#4ce35eff' }
];

const FIELDS_6M = [
    { key: 'at6mDegC', label: 'Temperature (°C)', color: '#EF6C00' },
    { key: 'rh6mPer', label: 'Humidity (%)', color: '#1565C0' },
    { key: 'rainFallMm', label: 'Rainfall (mm)', color: '#2E7D32' },
    { key: 'avgNetRadiationwBym2', label: 'Net Radiation', color: '#7B1FA2' },
    { key: 'ws6mMps', label: 'Wind Spd (m/s)', color: '#4E342E' },
    { key: 'wd6mDeg', label: 'Wind Dir (°)', color: '#4ce35eff' }
];

const FIELDS_COMBINED = [
    // 2m
    { key: 'at2mDegC', label: 'Temp (2m) °C', color: '#EF6C00' },
    { key: 'rh2mPer', label: 'Hum (2m) %', color: '#1565C0' },
    { key: 'rainFallMm', label: 'Rain (2m) mm', color: '#2E7D32' },
    { key: 'avgNetRadiationwBym2', label: 'Net Rad (2m)', color: '#7B1FA2' },
    { key: 'ws2mMps', label: 'Wind Spd (2m)', color: '#4E342E' },
    { key: 'wd2mDeg', label: 'Wind Dir (2m)', color: '#4ce35eff' },
    { key: 'ap2mHpa', label: 'Pressure (2m)', color: '#311B92' },
    // 4m
    { key: 'at4mDegC', label: 'Temp (4m) °C', color: '#FBC02D' },
    { key: 'rh4mPer', label: 'Hum (4m) %', color: '#03A9F4' },
    { key: 'ws4mMps', label: 'Wind Spd (4m)', color: '#8d6e63ff' },
    { key: 'wd4mDeg', label: 'Wind Dir (4m)', color: '#66bb6aff' },
    // 6m
    { key: 'at6mDegC', label: 'Temp (6m) °C', color: '#D84315' },
    { key: 'rh6mPer', label: 'Hum (6m) %', color: '#006064' },
    { key: 'ws6mMps', label: 'Wind Spd (6m)', color: '#795548' },
    { key: 'wd6mDeg', label: 'Wind Dir (6m)', color: '#26a69aff' }
];

const ALL_FIELDS = [...FIELDS_2M, ...FIELDS_4M, ...FIELDS_6M, ...FIELDS_COMBINED];

const YearlyChartSection = ({ title, fields, color, expanded, onToggle, visible, handleToggle, yearlyData, chartType }) => (
    <div className="glass-panel" style={{ padding: "1.5rem", marginBottom: "1.5rem", borderTop: `4px solid ${color}` }}>
        <div
            onClick={onToggle}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                marginBottom: expanded ? '1rem' : 0
            }}
        >
            <h3 style={{ fontSize: "1.2rem", fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                <span style={{ fontSize: '1.5rem' }}>📈</span> {title}
            </h3>
            <IconButton size="small">
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
        </div>

        <Collapse in={expanded}>
            {/* Parameter Checkboxes */}
            <FormGroup row style={{ marginBottom: 20, gap: 5, flexWrap: "wrap" }}>
                {fields.map(field => (
                    <FormControlLabel
                        key={field.key}
                        control={
                            <Checkbox
                                checked={visible[field.key] || false}
                                onChange={() => handleToggle(field.key)}
                                sx={{ color: field.color, "&.Mui-checked": { color: field.color } }}
                            />
                        }
                        label={field.label}
                        sx={{ margin: "2px" }}
                    />
                ))}
            </FormGroup>

            {/* CHART */}
            <div style={{ width: "100%", height: 350 }}>
                <ResponsiveContainer>
                    {chartType === 'line' ? (
                        <LineChart
                            data={yearlyData}
                            margin={{ top: 5, right: 30, left: 20, bottom: 30 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />

                            <XAxis
                                dataKey="year"
                                tick={{ fill: "var(--text-secondary)" }}
                                label={{
                                    value: "Year",
                                    position: "insideBottom",
                                    offset: -5,
                                    fill: "var(--text-secondary)"
                                }}
                            />

                            <YAxis
                                tick={{ fill: "var(--text-secondary)" }}
                                label={{
                                    value: "Values",
                                    angle: -90,
                                    position: "insideLeft",
                                    fill: "var(--text-secondary)"
                                }}
                            />

                            <Tooltip content={<CustomTooltip visibleParams={visible} />} />
                            <Legend />

                            {fields.map(field =>
                                visible[field.key] && !field.key.toLowerCase().startsWith('wd') ? (
                                    <Line
                                        key={field.key}
                                        type="monotone"
                                        dataKey={field.key}
                                        name={field.label}
                                        stroke={field.color}
                                        strokeWidth={3}
                                        dot={<CustomizedDot visibleParams={visible} />}
                                        activeDot={<CustomizedDot isActive={true} visibleParams={visible} />}
                                    />
                                ) : null
                            )}
                        </LineChart>
                    ) : (
                        <BarChart
                            data={yearlyData}
                            margin={{ top: 5, right: 30, left: 20, bottom: 30 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />

                            <XAxis
                                dataKey="year"
                                tick={{ fill: "var(--text-secondary)" }}
                                label={{
                                    value: "Year",
                                    position: "insideBottom",
                                    offset: -5,
                                    fill: "var(--text-secondary)"
                                }}
                            />

                            <YAxis
                                tick={{ fill: "var(--text-secondary)" }}
                                label={{
                                    value: "Values",
                                    angle: -90,
                                    position: "insideLeft",
                                    fill: "var(--text-secondary)"
                                }}
                            />

                            <Tooltip content={<CustomTooltip visibleParams={visible} />} />
                            <Legend />

                            {fields.map(field =>
                                visible[field.key] && !field.key.toLowerCase().startsWith('wd') ? (
                                    <Bar
                                        key={field.key}
                                        dataKey={field.key}
                                        name={field.label}
                                        fill={field.color}
                                        barSize={25}
                                    />
                                ) : null
                            )}
                        </BarChart>
                    )}
                </ResponsiveContainer>
            </div>
        </Collapse>
    </div>
);

const YearlyTrendChart = ({ data, loading, chartType }) => {
    // Default visible parameters
    const [visible, setVisible] = useState(
        ALL_FIELDS.reduce((acc, field) => ({
            ...acc,
            [field.key]: ['at2mDegC', 'rh2mPer', 'at4mDegC', 'rh4mPer', 'at6mDegC', 'rh6mPer', 'rainFallMm'].includes(field.key)
        }), {})
    );

    const handleToggle = (key) => {
        setVisible(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const [expandedSections, setExpandedSections] = useState({
        '2m': true,
        '4m': true,
        '6m': true,
        'combined': true
    });

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    if (loading) {
        return (
            <div className="glass-panel flex-center" style={{ height: 400 }}>
                Loading yearly trend...
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="glass-panel" style={{ padding: 30, textAlign: "center" }}>
                No yearly data available.
            </div>
        );
    }

    // Convert backend time → year
    const yearlyData = data.map(record => {
        let cleanTime = record.time?.replace(/\[.*?\]/, "") || "";
        let year = cleanTime ? new Date(cleanTime).getFullYear() : null;
        return { ...record, year };
    });

    return (
        <div style={{ paddingBottom: '2rem' }}>
            <h2 style={{ fontSize: "1.8rem", fontWeight: 700, color: "#1F5A36", marginBottom: "1.5rem", textAlign: 'center' }}>
                Yearly Weather Trends
            </h2>

            <YearlyChartSection
                title="Yearly Trends – 2m Depth"
                fields={FIELDS_2M}
                color="#1F5A36"
                expanded={expandedSections['2m']}
                onToggle={() => toggleSection('2m')}
                visible={visible}
                handleToggle={handleToggle}
                yearlyData={yearlyData}
                chartType={chartType}
            />
            <YearlyChartSection
                title="Yearly Trends – 4m Depth"
                fields={FIELDS_4M}
                color="#FF9016"
                expanded={expandedSections['4m']}
                onToggle={() => toggleSection('4m')}
                visible={visible}
                handleToggle={handleToggle}
                yearlyData={yearlyData}
                chartType={chartType}
            />
            <YearlyChartSection
                title="Yearly Trends – 6m Depth"
                fields={FIELDS_6M}
                color="#01383eff"
                expanded={expandedSections['6m']}
                onToggle={() => toggleSection('6m')}
                visible={visible}
                handleToggle={handleToggle}
                yearlyData={yearlyData}
                chartType={chartType}
            />
            <YearlyChartSection
                title="Combined Depth Trends (2m, 4m, 6m)"
                fields={FIELDS_COMBINED}
                color="#0288D1"
                expanded={expandedSections['combined']}
                onToggle={() => toggleSection('combined')}
                visible={visible}
                handleToggle={handleToggle}
                yearlyData={yearlyData}
                chartType={chartType}
            />
        </div>
    );
};

export default YearlyTrendChart;
